// Censorship menu component for the library

import React from 'react';
import {
  ChevronRightIcon,
  MessageSquareLock,
  ShieldAlert,
  ShieldPlus,
} from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
} from '../../../components/ui/popover';
import { Separator } from '../../../components/ui/separator';
import { Switch } from '../../../components/ui/switch';
import type { CensorshipViewModel } from '../hooks';
import BlockedTermsSyncIssueDialog from './BlockedTermsSyncAlertDialog';

type CensorshipMenuProps = {
  viewModel: CensorshipViewModel;
};

const CensorshipMenu: React.FC<CensorshipMenuProps> = ({ viewModel }) => {
  return (
    <>
      <BlockedTermsSyncIssueDialog
        open={viewModel.syncIssueOpen}
        onOpenChange={viewModel.onSyncIssueOpenChange}
        onRetry={viewModel.onRetrySync}
        onReconnect={viewModel.onReconnectTwitch}
      />
      <PopoverHeader>
        <PopoverTitle>Censorship</PopoverTitle>
        <PopoverDescription>
          Apply censorship to player-authored text, including names and game
          inputs.
        </PopoverDescription>
      </PopoverHeader>
      <Separator />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Common profanity</div>
            <p className="text-xs text-muted-foreground">
              Block words from our built-in dictionary.
            </p>
          </div>
          <Switch
            checked={viewModel.censorCommonChecked}
            onCheckedChange={viewModel.onCensorCommonToggle}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium">Custom profanity</div>
            <p className="text-xs text-muted-foreground">
              Block words from a custom list or terms you blocked on Twitch.
            </p>
          </div>
          <Switch
            checked={viewModel.censorCustomChecked}
            onCheckedChange={viewModel.onCensorCustomToggle}
          />
        </div>
        {viewModel.showCustomWordsRow && (
          <Button
            variant="ghost"
            className="h-auto w-full justify-start gap-2 px-2 py-2 text-left"
            onClick={viewModel.onCustomWordsRowClick}
          >
            <ShieldPlus />
            <span>Custom words</span>
            <Badge variant="secondary" className="ml-auto">
              {viewModel.customWordsBadgeLabel}
            </Badge>
            <ChevronRightIcon className="text-muted-foreground" />
          </Button>
        )}
        {viewModel.showBlockedTwitchTermsRow && (
          <Button
            variant="ghost"
            className="h-auto w-full justify-start gap-2 px-2 py-2 text-left"
            onClick={viewModel.onBlockedTwitchTermsRowClick}
          >
            <ShieldAlert />
            <span>Blocked on Twitch</span>
            {viewModel.blockedTwitchTermsBadgeLabel ? (
              <Badge variant="secondary" className="ml-auto">
                {viewModel.blockedTwitchTermsBadgeLabel}
              </Badge>
            ) : (
              <span className="ml-auto" />
            )}
            <ChevronRightIcon className="text-muted-foreground" />
          </Button>
        )}
        {viewModel.showRigidRow && (
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium">Rigid mode</div>
              <p className="text-xs text-muted-foreground">
                Catch profanity hidden inside other words. More strict, but may
                block innocent text.
              </p>
            </div>
            <Switch
              checked={viewModel.censorRigidChecked}
              onCheckedChange={viewModel.onCensorRigidToggle}
            />
          </div>
        )}
      </div>
    </>
  );
};

export const CENSORSHIP_MENU_ICON = <MessageSquareLock />;
export default CensorshipMenu;
